import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useOnlineCustomer from '../../hooks/useOnlineCustomer';
import useEmailBounceState from '../../hooks/useEmailBounceState';
import EditableList from '../EditableList/EditableList';
import EmailBounceStateIndicator from '../Mail/EmailBounceStateIndicator';

function fmt(val) {
  if (!val) return '—';
  return dayjs(val).format('DD.MM.YYYY HH:mm');
}

function SectionHeading({ children }) {
  return (
    <Typography
      variant='subtitle2'
      sx={{
        mb: 1,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        display: 'inline-block',
        fontWeight: 700,
        letterSpacing: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

function CustomerStatusChip({ customer, sx }) {
  if (customer?.globalUnsubscribe) {
    return <Chip label='Global abgemeldet' color='error' size='small' sx={sx} />;
  }
  if (customer?.emailBounceState?.isSuppressed) {
    return <Chip label='Nicht zustellbar' color='warning' size='small' sx={sx} />;
  }
  return <Chip label='Aktiv' color='success' size='small' sx={sx} />;
}

function ConsentHistoryTable({ entries }) {
  if (!entries || entries.length === 0) return <Typography variant='body2'>Keine Einträge.</Typography>;
  const sorted = [...entries].reverse();
  const typeLabel = {
    consent_granted: 'Einwilligung erteilt',
    consent_revoked: 'Einwilligung widerrufen',
    global_unsubscribe_set: 'Global abgemeldet',
    global_unsubscribe_cleared: 'Globale Abmeldung aufgehoben',
  };
  const typeColor = {
    consent_granted: 'success',
    consent_revoked: 'error',
    global_unsubscribe_set: 'error',
    global_unsubscribe_cleared: 'success',
  };
  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell>Zeitpunkt</TableCell>
          <TableCell>Ereignis</TableCell>
          <TableCell>Entertainment</TableCell>
          <TableCell>Quelle</TableCell>
          <TableCell>Detail</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map((e, i) => (
          <TableRow key={i}>
            <TableCell>{fmt(e.eventAt)}</TableCell>
            <TableCell>
              <Chip label={typeLabel[e.type] || e.type} color={typeColor[e.type] || 'default'} size='small' />
            </TableCell>
            <TableCell>{e.entertainmentName || '—'}</TableCell>
            <TableCell>{e.source || '—'}</TableCell>
            <TableCell>{e.detail || '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function DataHistoryTable({ entries }) {
  if (!entries || entries.length === 0) return <Typography variant='body2'>Keine Einträge.</Typography>;
  const sorted = [...entries].reverse();
  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell>Zeitpunkt</TableCell>
          <TableCell>Quelle</TableCell>
          <TableCell>Änderungen</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map((e, i) => (
          <TableRow key={i}>
            <TableCell>{fmt(e.changedAt)}</TableCell>
            <TableCell>{e.source || '—'}</TableCell>
            <TableCell>
              {(e.fields || []).map((f, fi) => (
                <Typography key={fi} variant='body2'>
                  <b>{f.field}</b>: {String(f.oldValue ?? '—')} → {String(f.newValue ?? '—')}
                </Typography>
              ))}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function MarketingConsentsTable({
  consents,
  canUpdate,
  onRevokeConsent,
  onGrantConsent,
  updatingConsentId,
}) {
  if (!consents || consents.length === 0)
    return <Typography variant='body2'>Keine Einwilligungen erfasst.</Typography>;
  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell>Entertainment</TableCell>
          <TableCell>Einwilligung</TableCell>
          <TableCell>Quelle</TableCell>
          <TableCell>Zuletzt aktualisiert</TableCell>
          <TableCell>GetResponse-Sync</TableCell>
          {canUpdate && <TableCell align='right'>Aktion</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {consents.map((c, i) => {
          const sync = c.getResponseSync || {};
          const entertainmentId = c.entertainmentId ? String(c.entertainmentId) : '';
          return (
            <TableRow key={i}>
              <TableCell>{c.entertainmentName || String(c.entertainmentId)}</TableCell>
              <TableCell>
                <Chip
                  label={c.hasConsent ? 'Ja' : 'Nein'}
                  color={c.hasConsent ? 'success' : 'default'}
                  size='small'
                />
              </TableCell>
              <TableCell>{c.source || '—'}</TableCell>
              <TableCell>{fmt(c.updatedAt)}</TableCell>
              <TableCell>
                <Chip label={sync.status || 'ausstehend'} size='small' />
                {sync.lastError ? (
                  <Typography variant='caption' color='error' display='block'>
                    {sync.lastError}
                  </Typography>
                ) : null}
              </TableCell>
              {canUpdate && (
                <TableCell align='right'>
                  {c.hasConsent ? (
                    <Button
                      size='small'
                      color='error'
                      variant='outlined'
                      startIcon={updatingConsentId === entertainmentId ? <CircularProgress size={14} /> : <DeleteIcon />}
                      disabled={!entertainmentId || updatingConsentId === entertainmentId}
                      onClick={() => onRevokeConsent?.(entertainmentId)}
                    >
                      Entfernen
                    </Button>
                  ) : (
                    <Button
                      size='small'
                      color='success'
                      variant='outlined'
                      startIcon={
                        updatingConsentId === entertainmentId ? (
                          <CircularProgress size={14} />
                        ) : (
                          <CheckCircleOutlineIcon />
                        )
                      }
                      disabled={!entertainmentId || updatingConsentId === entertainmentId}
                      onClick={() => onGrantConsent?.(entertainmentId)}
                    >
                      Hinzufügen
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

const SIGNUP_METHOD_LABEL = {
  manual: 'Manuell',
  'birthday-audience-sync': 'Audience-Sync',
  double_opt_in_web: 'Double-Opt-in (Web)',
  double_opt_in_qr: 'Double-Opt-in (QR)',
};

function signupMethodLabel(method) {
  if (!method) return '—';
  if (SIGNUP_METHOD_LABEL[method]) return SIGNUP_METHOD_LABEL[method];
  if (String(method).startsWith('double_opt_in_')) {
    return `Double-Opt-in (${String(method).slice('double_opt_in_'.length)})`;
  }
  return method;
}

function ListMembershipsTable({ memberships }) {
  if (!memberships || memberships.length === 0) {
    return <Typography variant='body2'>Keine Listen-Mitgliedschaften.</Typography>;
  }
  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell>Liste</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Anmeldeweg</TableCell>
          <TableCell>Beigetreten</TableCell>
          <TableCell>Entfernt</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {memberships.map((m, i) => (
          <TableRow key={m.membershipId || i}>
            <TableCell>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {m.listName || '—'}
                {m.isBirthdaySignupTarget ? (
                  <Chip label='Geburtstags-Club' color='success' size='small' />
                ) : null}
              </Box>
            </TableCell>
            <TableCell>
              <Chip
                label={m.active ? 'Aktiv' : 'Entfernt'}
                color={m.active ? 'success' : 'default'}
                size='small'
              />
            </TableCell>
            <TableCell>{signupMethodLabel(m.signupMethod)}</TableCell>
            <TableCell>{fmt(m.addedAt)}</TableCell>
            <TableCell>{m.active ? '—' : fmt(m.removedAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function VisitedEntertainmentsTable({ visits }) {
  if (!visits || visits.length === 0)
    return <Typography variant='body2'>Keine besuchten Entertainments.</Typography>;
  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell>Entertainment</TableCell>
          <TableCell>Erster Besuch</TableCell>
          <TableCell>Letzter Besuch</TableCell>
          <TableCell>Anzahl Buchungen</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {visits.map((v, i) => (
          <TableRow key={i}>
            <TableCell>{v.entertainmentName || String(v.entertainmentId)}</TableCell>
            <TableCell>{fmt(v.firstVisitAt)}</TableCell>
            <TableCell>{fmt(v.lastVisitAt)}</TableCell>
            <TableCell>{v.visitCount ?? 1}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CustomerDetailModal({
  customer,
  open,
  onClose,
  onBounceCleared,
  onRevokeConsent,
  onGrantConsent,
  updatingConsentId,
}) {
  const authCtx = useContext(AuthContext);
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const canClearBounce = authCtx.checkRight('emailBounceState:clear');
  const canUpdate = authCtx.checkRight('onlineCustomer:update');
  const { clearBounce } = useEmailBounceState();
  const [clearing, setClearing] = useState(false);

  const handleClearBounce = useCallback(async () => {
    if (!customer?.email) return;
    setClearing(true);
    try {
      await clearBounce(customer.email, 'Manuell freigegeben aus Online-Kundenverwaltung');
      toast.success('Adressensperre aufgehoben.');
      onBounceCleared?.(customer.email);
    } catch (e) {
      toast.error('Fehler beim Aufheben der Adressensperre.');
    } finally {
      setClearing(false);
    }
  }, [customer?.email, clearBounce, onBounceCleared]);

  if (!customer) return null;
  const ebs = customer.emailBounceState || null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isLight ? theme.palette.background.paper : theme.palette.grey[900],
          backgroundImage: 'none',
          color: isLight ? theme.palette.text.primary : theme.palette.grey[100],
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 1.5,
        }}
      >
        <Box>
          <Typography variant='h6' component='span'>
            {customer.email}
          </Typography>
          <CustomerStatusChip customer={customer} sx={{ ml: 1.5, verticalAlign: 'middle' }} />
        </Box>
        <IconButton onClick={onClose} size='small' sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 4, px: 3 }}>
        {ebs?.isSuppressed && (
          <Box
            sx={{
              mb: 2,
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'rgba(244, 67, 54, 0.15)',
              border: '1px solid',
              borderColor: 'error.dark',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant='body2' color='error.light' sx={{ flexGrow: 1 }}>
              <b>E-Mail-Adresse als nicht zustellbar gesperrt</b>
              {ebs.lastHardBounceAt
                ? ` – seit ${dayjs(ebs.lastHardBounceAt).format('DD.MM.YYYY HH:mm')}`
                : ''}
              {ebs.lastErrorMessage ? ` (${ebs.lastErrorMessage})` : ''}
            </Typography>
            {canClearBounce && (
              <Button
                size='small'
                variant='outlined'
                color='warning'
                startIcon={<LockOpenIcon />}
                onClick={handleClearBounce}
                disabled={clearing}
              >
                {clearing ? 'Wird aufgehoben…' : 'Sperre aufheben'}
              </Button>
            )}
          </Box>
        )}
        {!ebs?.isSuppressed && ebs?.softBounceCount > 0 && (
          <Box
            sx={{
              mb: 2,
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'rgba(255, 152, 0, 0.15)',
              border: '1px solid',
              borderColor: 'warning.dark',
            }}
          >
            <Typography variant='body2' color='warning.light'>
              {ebs.softBounceCount} Soft-Bounce(s)
              {ebs.lastSoftBounceAt
                ? ` (zuletzt ${dayjs(ebs.lastSoftBounceAt).format('DD.MM.YYYY HH:mm')})`
                : ''}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 3, mb: 2, mt:2, flexWrap: 'wrap' }}>
          {customer.firstName && (
            <Typography variant='body2' color='text.secondary'>
              Vorname:{' '}
              <Typography component='span' variant='body2' color='text.primary' sx={{ fontWeight: 700 }}>
                {customer.firstName}
              </Typography>
            </Typography>
          )}
          {customer.lastName && (
            <Typography variant='body2' color='text.secondary'>
              Nachname:{' '}
              <Typography component='span' variant='body2' color='text.primary' sx={{ fontWeight: 700 }}>
                {customer.lastName}
              </Typography>
            </Typography>
          )}
          {customer.phone && (
            <Typography variant='body2' color='text.secondary'>
              Telefon:{' '}
              <Typography component='span' variant='body2' color='text.primary' sx={{ fontWeight: 700 }}>
                {customer.phone}
              </Typography>
            </Typography>
          )}
          {customer.birthdate && (
            <Typography variant='body2' color='text.secondary'>
              Geburtstag:{' '}
              <Typography component='span' variant='body2' color='text.primary' sx={{ fontWeight: 700 }}>
                {dayjs(customer.birthdate).format('DD.MM.YYYY')}
              </Typography>
            </Typography>
          )}
          <Typography variant='body2' color='text.secondary'>
            Zuletzt geändert:{' '}
            <Typography component='span' variant='body2' color='text.primary' sx={{ fontWeight: 700 }}>
              {fmt(customer.updatedAt)}
            </Typography>
          </Typography>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Box sx={{ mb: 3 }}>
          <SectionHeading>Besuchte Entertainments</SectionHeading>
          <VisitedEntertainmentsTable visits={customer.visitedEntertainments} />
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Box sx={{ mb: 3 }}>
          <SectionHeading>Listen-Mitgliedschaften</SectionHeading>
          <ListMembershipsTable memberships={customer.listMemberships} />
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Box sx={{ mb: 3 }}>
          <SectionHeading>Marketing-Einwilligungen</SectionHeading>
          <MarketingConsentsTable
            consents={customer.marketingConsents}
            canUpdate={canUpdate}
            onRevokeConsent={onRevokeConsent}
            onGrantConsent={onGrantConsent}
            updatingConsentId={updatingConsentId}
          />
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Box sx={{ mb: 3 }}>
          <SectionHeading>Einwilligungs-History</SectionHeading>
          <ConsentHistoryTable entries={customer.consentHistory} />
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Box sx={{ mb: 1 }}>
          <SectionHeading>Datenänderungs-History</SectionHeading>
          <DataHistoryTable entries={customer.dataHistory} />
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default function OnlineCustomerManagement() {
  const authCtx = useContext(AuthContext);
  const { patchOnlineCustomer, deleteOnlineCustomer } = useOnlineCustomer();

  const canRead = authCtx.checkRight('onlineCustomer:read');
  const canUpdate = authCtx.checkRight('onlineCustomer:update');
  const canDelete = authCtx.checkRight('onlineCustomer:delete');

  const [detailCustomer, setDetailCustomer] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);
  const [deleteCustomer, setDeleteCustomer] = useState(null);
  const [blacklistCustomer, setBlacklistCustomer] = useState(null);
  const [confirmEditUnsubscribe, setConfirmEditUnsubscribe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [globalUnsub, setGlobalUnsub] = useState(false);
  const [updatingConsentId, setUpdatingConsentId] = useState(null);

  const emailRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const phoneRef = useRef(null);

  const handleEditOpen = useCallback(customer => {
    setGlobalUnsub(customer.globalUnsubscribe || false);
    setEditCustomer(customer);
  }, []);

  const handleRowOpenDetail = useCallback(item => {
    const raw = item?.__raw ?? item;
    if (raw) setDetailCustomer(raw);
  }, []);

  // Opens a confirmation before an edit turns the global unsubscribe on, saves directly otherwise
  const handleEditSaveClick = () => {
    if (globalUnsub && editCustomer?.globalUnsubscribe !== true) {
      setConfirmEditUnsubscribe(true);
      return;
    }
    handleEditSave();
  };

  const handleEditSave = async () => {
    setBusy(true);
    try {
      const email = String(emailRef.current?.value || '').trim().toLowerCase();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error('Bitte gib eine gültige E-Mail-Adresse ein.');
        return;
      }

      const updatedCustomer = await patchOnlineCustomer(editCustomer._id, {
        email,
        firstName: firstNameRef.current?.value || undefined,
        lastName: lastNameRef.current?.value || undefined,
        phone: phoneRef.current?.value || undefined,
        globalUnsubscribe: globalUnsub,
      });
      toast.success('Online-Kunde gespeichert.');
      setDetailCustomer(prev =>
        prev?._id === updatedCustomer?._id
          ? {
              ...updatedCustomer,
              emailBounceState:
                updatedCustomer.email === prev.email
                  ? updatedCustomer.emailBounceState || prev.emailBounceState || null
                  : updatedCustomer.emailBounceState || null,
            }
          : prev
      );
      setEditCustomer(null);
      window.dispatchEvent(new CustomEvent('editableListRefresh', { detail: { resource: 'onlineCustomer' } }));
    } catch (err) {
      if (err?.code === 'ONLINE_CUSTOMER_EMAIL_ALREADY_EXISTS') {
        toast.error('Diese E-Mail-Adresse ist bereits einem Online-Kunden zugeordnet.');
      } else if (err?.code === 'INVALID_ONLINE_CUSTOMER_EMAIL') {
        toast.error('Bitte gib eine gültige E-Mail-Adresse ein.');
      } else {
        toast.error('Fehler beim Speichern des Online-Kunden.');
      }
    } finally {
      setBusy(false);
    }
  };

  const applyBlacklist = useCallback(
    async (customer, newVal) => {
      setBusy(true);
      try {
        await patchOnlineCustomer(customer._id, { globalUnsubscribe: newVal });
        toast.success(newVal ? 'Kunde global abgemeldet.' : 'Globale Abmeldung aufgehoben.');
        setBlacklistCustomer(null);
        window.dispatchEvent(new CustomEvent('editableListRefresh', { detail: { resource: 'onlineCustomer' } }));
      } catch {
        toast.error('Fehler beim Ändern des Abmeldestatus.');
      } finally {
        setBusy(false);
      }
    },
    [patchOnlineCustomer]
  );

  // Global unsubscribing requires an explicit confirmation, lifting it is applied directly
  const handleToggleBlacklist = useCallback(
    customer => {
      if (customer.globalUnsubscribe) {
        applyBlacklist(customer, false);
        return;
      }
      setBlacklistCustomer(customer);
    },
    [applyBlacklist]
  );

  const handleRevokeConsent = useCallback(
    async entertainmentId => {
      if (!detailCustomer?._id || !entertainmentId) return;
      setUpdatingConsentId(entertainmentId);
      try {
        const updated = await patchOnlineCustomer(detailCustomer._id, {
          revokeMarketingConsentEntertainmentId: entertainmentId,
        });
        setDetailCustomer(prev => ({
          ...updated,
          emailBounceState: updated.emailBounceState || prev?.emailBounceState || null,
        }));
        toast.success('Marketing-Einwilligung entfernt.');
        window.dispatchEvent(new CustomEvent('editableListRefresh', { detail: { resource: 'onlineCustomer' } }));
      } catch {
        toast.error('Fehler beim Entfernen der Marketing-Einwilligung.');
      } finally {
        setUpdatingConsentId(null);
      }
    },
    [detailCustomer?._id, patchOnlineCustomer]
  );

  const handleGrantConsent = useCallback(
    async entertainmentId => {
      if (!detailCustomer?._id || !entertainmentId) return;
      setUpdatingConsentId(entertainmentId);
      try {
        const updated = await patchOnlineCustomer(detailCustomer._id, {
          grantMarketingConsentEntertainmentId: entertainmentId,
        });
        setDetailCustomer(prev => ({
          ...updated,
          emailBounceState: updated.emailBounceState || prev?.emailBounceState || null,
        }));
        toast.success('Marketing-Einwilligung hinzugefügt.');
        window.dispatchEvent(new CustomEvent('editableListRefresh', { detail: { resource: 'onlineCustomer' } }));
      } catch {
        toast.error('Fehler beim Hinzufügen der Marketing-Einwilligung.');
      } finally {
        setUpdatingConsentId(null);
      }
    },
    [detailCustomer?._id, patchOnlineCustomer]
  );

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteOnlineCustomer(deleteCustomer._id);
      toast.success('Online-Kunde gelöscht.');
      setDeleteCustomer(null);
      window.dispatchEvent(new CustomEvent('editableListRefresh', { detail: { resource: 'onlineCustomer' } }));
    } catch {
      toast.error('Fehler beim Löschen des Online-Kunden.');
    } finally {
      setBusy(false);
    }
  };

  const schema = useMemo(
    () => ({
      email: {
        label: 'E-Mail',
        type: 'text',
        readOnly: true,
        minWidth: '260px',
        listDisplayValue: (value, item) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title='Details anzeigen'>
              <IconButton
                size='small'
                onClick={e => {
                  e.stopPropagation();
                  handleRowOpenDetail(item);
                }}
                sx={{ flexShrink: 0 }}
              >
                <OpenInNewIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            {value || '—'}
            <EmailBounceStateIndicator state={item?.emailBounceState} showSoftBounces />
          </Box>
        ),
      },
      firstName: { label: 'Vorname', type: 'text', readOnly: true, listDisplayValue: v => v || '—' },
      lastName: { label: 'Nachname', type: 'text', readOnly: true, listDisplayValue: v => v || '—' },
      phone: { label: 'Telefon', type: 'text', readOnly: true, listDisplayValue: v => v || '—' },
      birthdate: {
        label: 'Geburtstag',
        type: 'text',
        readOnly: true,
        listDisplayValue: v => (v ? dayjs(v).format('DD.MM.YYYY') : '—'),
        sortType: 'date',
        sortValue: v => (v ? Date.parse(String(v)) : null),
      },
      birthdayClubMember: {
        label: 'Geburtstags-Club',
        type: 'boolean',
        readOnly: true,
        filterType: 'boolean',
        sortType: 'boolean',
        filterValue: (_value, item) => (item?.__raw || item)?.birthdayClubMember === true,
        sortValue: (_value, item) => ((item?.__raw || item)?.birthdayClubMember === true ? 1 : 0),
        listDisplayValue: (_value, item) => {
          const member = (item?.__raw || item)?.birthdayClubMember === true;
          return (
            <Chip
              label={member ? 'Ja' : 'Nein'}
              color={member ? 'success' : 'default'}
              size='small'
            />
          );
        },
      },
      globalUnsubscribe: {
        label: 'Status',
        type: 'text',
        filterType: 'boolean',
        sortType: 'boolean',
        readOnly: true,
        filterValue: (_value, item) => (item?.__raw || item)?.globalUnsubscribe === true,
        sortValue: (_value, item) => ((item?.__raw || item)?.globalUnsubscribe === true ? 1 : 0),
        listDisplayValue: (_value, item) => <CustomerStatusChip customer={item?.__raw || item} />,
      },
      updatedAt: {
        label: 'Zuletzt geändert',
        type: 'text',
        readOnly: true,
        listDisplayValue: value => fmt(value),
        sortType: 'date',
        sortValue: v => (v ? Date.parse(String(v)) : null),
      },
      emailBounceState: { label: 'emailBounceState', type: 'object', excludeFromList: true },
      __raw: { label: '__raw', type: 'object', excludeFromList: true },
    }),
    [busy, canUpdate, handleRowOpenDetail]
  );

  const transformItems = useCallback(
    item => ({
      _id: item._id,
      email: item.email,
      firstName: item.firstName || '',
      lastName: item.lastName || '',
      phone: item.phone || '',
      birthdate: item.birthdate || null,
      birthdayClubMember: item.birthdayClubMember === true,
      globalUnsubscribe: item.globalUnsubscribe || false,
      updatedAt: item.updatedAt,
      emailBounceState: item.emailBounceState || null,
      __raw: item,
    }),
    []
  );

  const renderRowActions = useCallback(
    item => {
      const customer = item.__raw || item;
      return (
        <>
          {canUpdate && (
            <Tooltip title={customer.globalUnsubscribe ? 'Blacklist aufheben' : 'Global blacklisten'}>
              <span>
                <IconButton
                  size='small'
                  color={customer.globalUnsubscribe ? 'success' : 'warning'}
                  onClick={() => handleToggleBlacklist(customer)}
                  disabled={busy}
                >
                  {customer.globalUnsubscribe ? (
                    <CheckCircleOutlineIcon fontSize='small' />
                  ) : (
                    <BlockIcon fontSize='small' />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canUpdate && (
            <Tooltip title='Bearbeiten'>
              <IconButton size='small' onClick={() => handleEditOpen(customer)} disabled={busy}>
                <EditIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title='Löschen'>
              <IconButton size='small' color='error' onClick={() => setDeleteCustomer(customer)} disabled={busy}>
                <DeleteIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </>
      );
    },
    [canUpdate, canDelete, busy, handleToggleBlacklist, handleEditOpen]
  );

  if (!canRead) return <Navigate to='/welcome' replace />;

  return (
    <>
      <EditableList
        title='Online-Kunden'
        resourceType='onlineCustomer'
        schema={schema}
        searchPlaceholder='Online-Kunden durchsuchen'
        options={{
          disableCreate: true,
          disableDelete: true,
          disableDetailView: true,
          disableInlineEditing: true,
          listConfig: {
            endpoint: '/api/v1/online-customer',
            searchParam: 'search',
            resultsKey: 'customers',
            pageParam: 'page',
            limitParam: 'limit',
          },
          transformItems,
          serverSideFiltering: true,
          serverSideSorting: true,
        }}
        renderRowActions={renderRowActions}
        onRowClick={handleRowOpenDetail}
      />

      <CustomerDetailModal
        customer={detailCustomer}
        open={Boolean(detailCustomer)}
        onClose={() => setDetailCustomer(null)}
        onRevokeConsent={handleRevokeConsent}
        onGrantConsent={handleGrantConsent}
        updatingConsentId={updatingConsentId}
        onBounceCleared={() => {
          setDetailCustomer(prev =>
            prev ? { ...prev, emailBounceState: { ...(prev.emailBounceState || {}), isSuppressed: false } } : prev
          );
          window.dispatchEvent(
            new CustomEvent('editableListRefresh', { detail: { resource: 'onlineCustomer' } })
          );
        }}
      />

      <Dialog
        open={Boolean(editCustomer)}
        onClose={() => {
          setEditCustomer(null);
          setConfirmEditUnsubscribe(false);
        }}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Online-Kunde bearbeiten</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label='E-Mail'
            type='email'
            inputRef={emailRef}
            defaultValue={editCustomer?.email || ''}
            size='small'
            fullWidth
            required
          />
          <TextField
            label='Vorname'
            inputRef={firstNameRef}
            defaultValue={editCustomer?.firstName || ''}
            size='small'
            fullWidth
          />
          <TextField
            label='Nachname'
            inputRef={lastNameRef}
            defaultValue={editCustomer?.lastName || ''}
            size='small'
            fullWidth
          />
          <TextField
            label='Telefon'
            inputRef={phoneRef}
            defaultValue={editCustomer?.phone || ''}
            size='small'
            fullWidth
          />
          <FormControlLabel
            control={<Switch checked={globalUnsub} onChange={e => setGlobalUnsub(e.target.checked)} />}
            label='Global abgemeldet (überschreibt alle Einwilligungen)'
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditCustomer(null);
              setConfirmEditUnsubscribe(false);
            }}
            disabled={busy}
          >
            Abbrechen
          </Button>
          <Button variant='contained' onClick={handleEditSaveClick} disabled={busy}>
            {busy ? <CircularProgress size={18} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(blacklistCustomer)} onClose={() => setBlacklistCustomer(null)}>
        <DialogTitle>Kunde global abmelden?</DialogTitle>
        <DialogContent>
          <Typography>
            Soll <b>{blacklistCustomer?.email}</b> global abgemeldet werden? Damit werden alle erteilten
            Marketing-Einwilligungen überschrieben und der Kunde erhält keine Mailings mehr.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlacklistCustomer(null)} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='warning'
            onClick={() => applyBlacklist(blacklistCustomer, true)}
            disabled={busy}
          >
            {busy ? <CircularProgress size={18} /> : 'Global abmelden'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmEditUnsubscribe} onClose={() => setConfirmEditUnsubscribe(false)}>
        <DialogTitle>Kunde global abmelden?</DialogTitle>
        <DialogContent>
          <Typography>
            Soll <b>{editCustomer?.email}</b> global abgemeldet werden? Damit werden alle erteilten
            Marketing-Einwilligungen überschrieben und der Kunde erhält keine Mailings mehr.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEditUnsubscribe(false)} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='warning'
            onClick={() => {
              setConfirmEditUnsubscribe(false);
              handleEditSave();
            }}
            disabled={busy}
          >
            {busy ? <CircularProgress size={18} /> : 'Speichern & abmelden'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteCustomer)} onClose={() => setDeleteCustomer(null)}>
        <DialogTitle>Online-Kunde löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Soll der Datensatz für <b>{deleteCustomer?.email}</b> endgültig gelöscht werden?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCustomer(null)} disabled={busy}>
            Abbrechen
          </Button>
          <Button variant='contained' color='error' onClick={handleDelete} disabled={busy}>
            {busy ? <CircularProgress size={18} /> : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
